import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { withSlash } from '../utils/withSlash';
import { Menu, X, Search, Home, Heart, TrendingDown, Mail, MapPin, BookOpen, Star, HelpCircle, Disc3, Info, FileText, Store, Coffee } from 'lucide-react';
import FollowUs from './FollowUs';
import { AffiliateDisclosure } from './AffiliateDisclosure';
import { track, trackNavClick, trackStoreClick, CLICK_SOURCES } from '../utils/analytics';
import { GUIDES } from '../data/guides';

// Buy Me a Coffee page (2026-10-31). Optional support link in the footer.
// Not an affiliate link, so it stays out of partners.js and gets no badge.
// Same URL lives in src/pages/About.jsx; change both if it ever moves.
const SUPPORT_URL = 'https://buymeacoffee.com/digginginthesalescrates';

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  // Phase 2 (Homepage & Content Hub) swapped "Blog" out of the nav for
  // "Watch & Read" (/watch-read). Blog is back as its own link (2026-10-09),
  // right after Watch & Read: the 18 posts were only reachable through
  // "Read all stories" on /watch-read, and a sitewide link gives every post
  // a short path from every page, which helps indexing. The footer row below
  // is built from this same array, so it picks the link up too.
  // Keep the crawler nav list in scripts/prerender.mjs in step with this.
  const navLinks = [
    { to: '/',                   label: 'Home',              icon: Home },
    // /search is the one search URL (2026-10-01). /aggregator was retired
    // and 301s to /search/ via public/_redirects.
    { to: '/search',             label: 'Vinyl Search',      icon: Search },
    { to: '/artists',            label: 'Artists',           icon: Disc3 },
    { to: '/deals',              label: 'Deals',             icon: TrendingDown },
    { to: '/watch-read',         label: 'Watch & Read',      icon: BookOpen },
    { to: '/blog',               label: 'Blog',              icon: FileText },
    // Renamed from "Where's My Shop?" (2026-10-29). That label read like a
    // store-owner portal; the page is a zip-code finder for shoppers.
    { to: '/local-shops',        label: 'Find Record Shops', icon: MapPin },
    { to: '/wishlist',           label: 'Wishlist',          icon: Heart },
    { to: '/faq',                label: 'FAQ',               icon: HelpCircle },
  ];

  // Footer and hamburger menu only (2026-10-09). Eleven header links plus
  // the NEW badge did not fit at 1366px, so About and Featured Partners
  // left the header row. They stay one click away in the footer and in the
  // mobile menu. Header order is unchanged for everything else.
  // "Partner Shops" (/shops, 2026-10-29) is here and not in the header for
  // the same reason: the header row is already at its width limit.
  const extraLinks = [
    { to: '/shops',              label: 'Partner Shops',     icon: Store },
    { to: '/featured-partners',  label: 'For Record Shop Owners', icon: Star },
    { to: '/about',              label: 'About',             icon: Info },
  ];
  const menuLinks = [...navLinks, ...extraLinks];

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-deep)', overflowX: 'hidden', width: '100%' }}>

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(10,10,15,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : '1px solid transparent',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: 64, gap: 32 }}>

            {/* Logo — gold record PNG cropped in circle + wordmark */}
            <Link to="/" className="nav-logo" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <img
                src="/ditsc-logo-small.webp"
                alt="DITSC"
                width={42}
                height={42}
                style={{
                  width: 42, height: 42, borderRadius: '50%',
                  objectFit: 'cover', objectPosition: 'center center',
                  flexShrink: 0, pointerEvents: 'none',
                }}
              />
              <div className="nav-logo-text" style={{
                fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: 2,
                lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden',
              }}>
                DIGGING IN THE <span style={{ color: 'var(--amber)' }}>SALES CRATES</span>
              </div>
            </Link>

            {/* Desktop Nav. flex:1 + space-evenly (2026-10-09) spreads the nine
                links across the free space so there is no dead gap after the
                logo; on narrow widths it packs tight like before. */}
            <div className="desktop-nav" style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'space-evenly', alignItems: 'center' }}>
              {navLinks.map(({ to, label, icon: Icon, comingSoon }) => (
                <Link key={to} to={withSlash(to)}
                  onClick={() => trackNavClick(label, to, 'header_desktop')}
                  style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500,
                  color: isActive(to) ? 'var(--amber)' : 'var(--text-primary)',
                  background: isActive(to) ? 'var(--amber-glow)' : 'transparent',
                  transition: 'all 0.2s',
                  border: isActive(to) ? '1px solid rgba(245,158,11,0.2)' : '1px solid transparent',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                }}>
                  <Icon size={14} />
                  {label}
                  {comingSoon && (
                    <span style={{
                      fontSize: 8, fontWeight: 700, letterSpacing: 0.5,
                      background: 'rgba(245,158,11,0.2)', color: 'var(--amber)',
                      padding: '1px 5px', borderRadius: 4,
                      fontFamily: 'var(--font-mono)',
                    }}>
                      NEW
                    </span>
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="mobile-toggle"
              aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={mobileOpen}
              style={{ color: 'var(--text-primary)', padding: 8, borderRadius: 8, marginLeft: 'auto' }}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{
            background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)',
            padding: '16px 24px',
          }}>
            {menuLinks.map(({ to, label, icon: Icon, comingSoon }) => (
              <Link key={to} to={withSlash(to)}
                onClick={() => trackNavClick(label, to, 'header_mobile')}
                style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 0', color: isActive(to) ? 'var(--amber)' : 'var(--text-primary)',
                borderBottom: '1px solid var(--border)', fontSize: 15,
              }}>
                <Icon size={18} />
                {label}
                {comingSoon && (
                  <span style={{
                    fontSize: 9, fontWeight: 700,
                    background: 'rgba(245,158,11,0.2)', color: 'var(--amber)',
                    padding: '1px 6px', borderRadius: 4,
                    fontFamily: 'var(--font-mono)',
                  }}>
                    NEW
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Responsive styles */}
      <style>{`
        /* Header breakpoints. Measured Sep 25 2026 with the real fonts for
           ten links: about 1640px with icons, about 1215px without.
           Oct 9 2026: the header row is now nine links (Blog added; About
           and Featured Partners moved to the footer and hamburger menu), so
           it is narrower than the measured ten: roughly 1510px with icons and
           1080px without. The thresholds below are ESTIMATES with margin, not
           measurements. Icons hide below 1600px; the hamburger takes over
           below 1200px. If the last link clips, raise 1599 / 1199.
           Oct 29 2026: "Where's My Shop?" became "Find Record Shops", three
           characters longer, so check the last header link at 1200px to
           1366px widths after this deploys. */
        @media (max-width: 1599px) {
          .desktop-nav a { padding: 8px 9px !important; }
          .desktop-nav a svg { display: none !important; }
        }
        @media (max-width: 1199px) {
          .desktop-nav { display: none !important; }
          .mobile-toggle { display: block !important; }
        }
        @media (max-width: 900px) {
          .nav-logo-text { font-size: 15px !important; letter-spacing: 1px !important; }
        }
        @media (min-width: 1200px) {
          .mobile-toggle { display: none !important; }
        }

        /* CLS FIX (Aug 2026)
           Cloudflare RUM attributed repeated layout shifts to #root>div>footer
           (0.221 / 0.267 / 0.323) on data-driven routes: /artists, /watch-read,
           /blog/*, /search. Root cause is not the footer itself — the footer was
           being pushed down when async page content mounted underneath a short
           <main>. Reserving a full-viewport minimum height on <main> keeps the
           footer below the fold during hydration, so late-arriving content no
           longer produces a visible shift. */
        .site-main {
          min-height: calc(100vh - 64px);
        }

        .record-card { transition: border-color 0.3s, transform 0.3s; }
        .record-card:hover { border-color: var(--border-hover) !important; transform: translateY(-2px); }
        .view-deals-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 8px; font-size: 12px; font-weight: 600;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #000; text-decoration: none; transition: opacity 0.2s;
        }
        .view-deals-btn:hover { opacity: 0.85; }
        .store-card { transition: border-color 0.3s, transform 0.3s; text-decoration: none; }
        .store-card:hover { border-color: rgba(245,158,11,0.4) !important; transform: translateY(-3px); }
        .footer-logo-wrap { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 12px; min-height: 52px; }
        .footer-email-link { color: var(--amber); font-size: 13px; font-family: var(--font-mono); letter-spacing: 1px; text-decoration: none; transition: opacity 0.2s; }
        .footer-email-link:hover { opacity: 0.7; }
        .filter-btn { padding: 5px 16px; border-radius: 100px; font-size: 12px; font-weight: 500; background: var(--bg-card); border: 1px solid var(--border); color: var(--text-secondary); text-transform: capitalize; cursor: pointer; transition: border-color 0.2s, color 0.2s, background 0.2s; }
        .filter-btn-active { background: var(--amber-glow); border-color: rgba(245,158,11,0.4) !important; color: var(--amber) !important; }
        .search-input:focus { border-color: var(--amber) !important; }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0px 1000px var(--bg-card) inset !important;
          -webkit-text-fill-color: var(--text-primary) !important;
          caret-color: var(--text-primary);
        }
      `}</style>

      {/* Main */}
      <main className="site-main" style={{ paddingTop: 64, overflowX: 'hidden', width: '100%' }}>
        {children}
      </main>

      <FollowUs />

      {/* Footer */}
      {/* marginTop was 80, which stacked on top of the last section's own
          bottom padding and produced the largest gap on the page. 48 keeps
          the footer visually separate without the canyon. */}
      <footer style={{
        borderTop: '1px solid var(--border)', marginTop: 48,
        padding: '36px 24px', textAlign: 'center', height: 'auto',
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="footer-logo-wrap">
            <img
              src="/ditsc-logo-small.webp"
              alt="DITSC"
              width={52}
              height={52}
              style={{
                width: 52, height: 52, borderRadius: '50%',
                objectFit: 'cover', objectPosition: 'center center',
                flexShrink: 0,
              }}
            />
            <span style={{ fontFamily: 'var(--font-display)', letterSpacing: 1, fontSize: 18 }}>
              DIGGING IN THE <span style={{ color: 'var(--amber)' }}>SALES CRATES</span>
            </span>
          </div>

          {/* Footer nav links */}
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            {menuLinks.map(({ to, label }) => (
              <Link key={to} to={withSlash(to)}
                onClick={() => trackNavClick(label, to, 'footer')}
                style={{ fontSize: 11, color: 'var(--text-primary)', textDecoration: 'none' }}>
                {label}
              </Link>
            ))}
          </div>

          {/* Guide pages (2026-10-04). Built from src/data/guides.js, so a
              new guide shows up here automatically. Puts every guide one
              click from any page, for visitors and for crawlers. */}
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, color: 'var(--amber)', fontFamily: 'var(--font-mono)',
              letterSpacing: 2,
            }}>
              GUIDES
            </span>
            {GUIDES.map((g) => (
              <Link key={g.slug} to={withSlash(g.path)}
                onClick={() => trackNavClick(g.title, g.path, 'footer')}
                style={{ fontSize: 11, color: 'var(--text-primary)', textDecoration: 'none' }}>
                {g.title}
              </Link>
            ))}
          </div>

          {/* Optional support link. Fires support_click (the same event the
              About page uses), not store_click, so it never shows up in the
              marketplace or NO_LEAK reports. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer"
              onClick={() => track('support_click', { click_source: 'footer', destination: SUPPORT_URL })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11, color: 'var(--text-primary)', textDecoration: 'none',
              }}>
              <Coffee size={12} aria-hidden="true" />
              Buy Me a Coffee
            </a>
          </div>

          <div style={{ marginBottom: 16 }}>
            <p style={{ color: 'var(--text-primary)', fontSize: 13, marginBottom: 6 }}>
              Store owner, label, or affiliate inquiry?
            </p>
            <a href="mailto:hello@digginginthesalescrates.com" className="footer-email-link"
              onClick={() => trackStoreClick({
                storeName: 'Partnership inquiry (footer)',
                storeUrl: 'mailto:hello@digginginthesalescrates.com',
                clickSource: CLICK_SOURCES.FOOTER,
                isAffiliate: false,
                notify: 'Someone clicked the partnership inquiry email',
              })}>
              hello@digginginthesalescrates.com
            </a>
          </div>
          <p style={{ color: 'var(--text-primary)', fontSize: 12 }}>
            © 2026 Digging in the Sales Crates. Find your perfect record.
          </p>
          <p style={{ color: 'var(--text-primary)', fontSize: 11, marginTop: 6 }}>
            Listing and pricing data via Discogs, eBay, CDandLP &amp; Turntable Lab. Not affiliated with, sponsored by, or endorsed by any listed marketplace or store.
          </p>

          {/* SITE-WIDE AFFILIATE DISCLOSURE.
              This is the baseline only. The FTC requires disclosure close to
              the link, so a footer line alone is NOT sufficient: the per-link
              badges on search results, the deals page, the wishlist, and blog
              posts are what actually satisfy that. Do not remove those on the
              grounds that this exists.

              The partner names in this sentence are generated from
              src/config/partners.js, so adding or dropping a program updates
              this text without anyone editing prose. */}
          <AffiliateDisclosure variant="footer" />
        </div>
      </footer>
    </div>
  );
}
